import React, { useCallback, useMemo, useState } from "react";
import { StyledDrawerTnC, StyledLogin } from "./styled";
import { useForm } from "react-hook-form";
import Otp from "./Otp";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { ClipLoader } from "react-spinners";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getEncipherencode,
  hasValidEncipherencode,
} from "utils/encipherencode";
import Footer from "components/v2/Common/Footer";
import { LazyLoadImage } from "react-lazy-load-image-component";
import LoginForm from "./LoginForm";
import TermsContent from "./TermsContent";
import useLoginFlow from "./useLoginFlow";
import LoginErrorBoundary from "./LoginErrorBoundary";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BottomSheetDrawer from "components/v2/Common/BottomSheetDrawer";
import FloatingCloseButton from "components/v2/Common/FloatingCloseButton";

function Login({ loginLoader, setLoginLoader, loadConfigurations }) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm();
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const navigate = useNavigate();
  const [tnc, setTnc] = useState(false);
  const {
    confirmOtp,
    setConfirmOtp,
    inputValue,
    response,
    setResponse,
    otpSendError,
    otpLoading,
    loginFieldContent,
    loginHandler,
  } = useLoginFlow({
    rmsConfig,
    params,
    encipherencode,
    dispatch,
    navigate,
    setValue,
    setLoginLoader,
  });
  const theme = merchantConfig?.public_page_theme_configurations || {};

  const handleOpenTerms = useCallback(() => {
    setTnc(true);
  }, []);

  const handleCloseTerms = useCallback(() => {
    setTnc(false);
  }, []);

  const getHelperText = useCallback(
    (content) => {
      if (!errors.customerIdentifier) {
        return content.helperText;
      }

      return errors.customerIdentifier.type === "pattern"
        ? content.patternErrorText
        : content.requiredErrorText;
    },
    [errors.customerIdentifier],
  );

  const handleLoginSubmit = useMemo(
    () => handleSubmit(loginHandler),
    [handleSubmit, loginHandler],
  );

  const returnToStoreUrl = useMemo(() => {
    if (!merchantConfig?.shopify_domain) {
      return "";
    }

    if (/^https?:\/\//i.test(merchantConfig.shopify_domain)) {
      return merchantConfig.shopify_domain;
    }

    return `https://${merchantConfig.shopify_domain}`;
  }, [merchantConfig?.shopify_domain]);

  const returnToStoreLink = useMemo(() => {
    if (!returnToStoreUrl || !merchantConfig?.shop_display_name) {
      return null;
    }

    return (
      <a
        className="return-to-store-link"
        href={returnToStoreUrl}
        style={{
          background:
            merchantConfig.merchant_id === 977
              ? "#DFE6F7"
              : `${merchantConfig.primary_theme_color}33`,
        }}
      >
        <span className="return-to-store-arrow" aria-hidden="true">
          <ArrowBackIcon style={{ fontSize: "16px" }} />
        </span>
        <span className="return-to-store-text">
          {`Back to ${merchantConfig.shop_display_name}`}
        </span>
      </a>
    );
  }, [
    merchantConfig?.merchant_id,
    merchantConfig?.primary_theme_color,
    merchantConfig?.shop_display_name,
    returnToStoreUrl,
  ]);

  const textFieldProps = useMemo(
    () => ({
      InputProps: {
        disableUnderline: false,
        sx: {
          "&:before": {
            borderBottom: `1px solid ${merchantConfig.primary_theme_color}`,
          },
          "&:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottom: `1px solid ${merchantConfig.primary_theme_color}`,
          },
          "&.Mui-focused:after": {
            borderBottom: `2px solid ${merchantConfig.primary_theme_color}`,
          },
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px"
            : "0px",
        },
      },
    }),
    [
      merchantConfig.primary_theme_color,
      merchantConfig.use_rounded_edge_buttons,
    ],
  );

  const buttonSx = useMemo(
    () => ({
      "& .MuiButton-endIcon": {
        marginLeft: "auto",
        marginRight: 0,
      },
    }),
    [],
  );

  const termsProps = useMemo(
    () => ({
      title: t("login.termsTitle"),
      content: rmsConfig.tnc,
      onClose: handleCloseTerms,
      titleStyle: { fontSize: "14px", fontWeight: "600" },
      closeTextStyle: {
        textAlign: "right",
        fontSize: "12px",
        cursor: "pointer",
      },
      closeIconStyle: {
        width: "18px",
        height: "18px",
        stroke: "#333",
      },
      contentContainerStyle: {
        maxHeight: "min(70vh, 620px)",
        overflow: "auto",
        listStylePosition: "outside",
        paddingRight: "10px",
      },
      showCloseButton: false,
    }),
    [handleCloseTerms, rmsConfig.tnc, t],
  );

  const loginContent = confirmOtp ? (
    <Otp
      inputValue={inputValue}
      setConfirmOtp={setConfirmOtp}
      response={response}
      setResponse={setResponse}
    />
  ) : (
    <section className="login-section">
      {theme.login_section_brand_logo_visible_in_mobile && (
        <LazyLoadImage
          effect="opacity"
          src={merchantConfig.brand_logo}
          className="brand-logo"
          alt={merchantConfig.shop_display_name}
        />
      )}
      <LoginForm
        title={rmsConfig.home_page_request_creation_title}
        control={control}
        errors={errors}
        onSubmit={handleLoginSubmit}
        otpLoading={otpLoading}
        otpSendError={otpSendError}
        merchantConfig={merchantConfig}
        fieldContent={loginFieldContent.light}
        variant="standard"
        helperTextResolver={getHelperText}
        brandHomePageNote={rmsConfig.brand_home_page_note}
        onOpenTerms={handleOpenTerms}
        textFieldProps={textFieldProps}
        buttonRadius={!merchantConfig.use_rounded_edge_buttons ? "0px" : "10px"}
        buttonStyle={{
          padding: "10px 1rem",
          fontSize: "16px",
          fontWeight: "600",
          marginTop: "20px",
        }}
        buttonSx={buttonSx}
        arrowTop="-1px"
        termsTextStyle={{
          margin: "20px auto 12px",
          color: "#000000B2",
          fontSize: "12px",
        }}
        termsLinkStyle={{
          color: "#1976d2",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: "600",
        }}
        afterTermsContent={returnToStoreLink}
      />
      {merchantConfig?.is_pragma_branding_visible && <Footer />}
    </section>
  );

  return (
    <div className="v2-login-page">
      {loginLoader ||
      !loadConfigurations ||
      !hasValidEncipherencode(encipherencode) ? (
        <div
          className="login-loader"
          style={{
            width: "100%",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ClipLoader
            color="#333"
            size={50}
            speedMultiplier={0.75}
            cssOverride={{ borderWidth: "6px" }}
          />
        </div>
      ) : (
        <StyledLogin
          themeColor={merchantConfig.primary_theme_color}
          rounded={merchantConfig.use_rounded_edge_buttons}
        >
          <LoginErrorBoundary>
            {theme.cover_image_visible_in_mobile && (
              <section className="cover-section">
                <LazyLoadImage
                  effect="opacity"
                  src={merchantConfig.brand_cover_image}
                  alt={merchantConfig.shop_display_name}
                />
              </section>
            )}
            {loginContent}
          </LoginErrorBoundary>
          <BottomSheetDrawer
            open={tnc}
            onClose={handleCloseTerms}
            transitionDuration={260}
            ModalProps={{ keepMounted: true }}
            rounded={merchantConfig.use_rounded_edge_buttons}
            paperClassName="terms-bottom-sheet"
            paperStyle={{
              background: "var(--rms-v2-surface, #fff)",
              backgroundColor: "#fff",
              boxShadow: "0 -14px 30px rgba(16, 24, 40, 0.18)",
            }}
            paperSx={{
              backgroundColor: "var(--rms-v2-surface, #fff) !important",
              overflow: "visible",
            }}
          >
            <StyledDrawerTnC>
              <FloatingCloseButton
                ariaLabel="Close terms and conditions"
                className="terms-drawer-close"
                iconSize={18}
                onClick={handleCloseTerms}
              />
              <TermsContent {...termsProps} />
            </StyledDrawerTnC>
          </BottomSheetDrawer>
        </StyledLogin>
      )}
    </div>
  );
}

export default Login;
